import type { ContractingEntity, ContractType } from "@contract-manager/shared";
import type { MessageKey } from "./i18n/messages";
import type { Translate } from "./i18n/translate";

/**
 * Labels taken from the legal team's Contract Tracker.
 *
 * The ledger stores its own codes; these are what the tracker's columns say,
 * so a row on screen and a row in their spreadsheet read the same. Kept apart
 * from the schema because a label is a display decision — renaming a column
 * header should not be a migration.
 */

/**
 * A tracker spelling, shown the same in every language, or — for the answers
 * the tracker has no code for — a word to translate.
 */
export type TrackerLabel =
  | { readonly text: string }
  | { readonly key: MessageKey };

/** `Soracom Entity` — SJP / SUK / SUS. */
export const ENTITY_LABEL: Record<ContractingEntity, TrackerLabel> = {
  JP: { text: "SJP" },
  UK: { text: "SUK" },
  US: { text: "SUS" },
  UNKNOWN: { key: "contracts.tracker.unknownEntity" },
};

/** `Contract Type`, spelled the way the tracker spells it. */
export const CONTRACT_TYPE_LABEL: Record<ContractType, TrackerLabel> = {
  NDA: { text: "NDA" },
  SERVICE: { text: "Service Agmt" },
  CONSULTANT: { text: "Consultant Agmt" },
  REFERRAL: { text: "Referral Agmt" },
  SUPPLEMENTAL: { text: "Supplemental Agmt" },
  OTHER: { key: "contracts.tracker.otherType" },
};

export function readTrackerLabel(
  label: TrackerLabel,
  t: Translate<MessageKey>,
): string {
  return "text" in label ? label.text : t(label.key);
}

/**
 * `Renewal Period` — a count of months as "1 yr" or "18 months", matching how
 * the tracker writes it.
 */
export function formatRenewalTerm(months: number | null): string | null {
  if (months === null) {
    return null;
  }
  return months % 12 === 0 ? `${months / 12} yr` : `${months} months`;
}

/** `Notification Deadline` — "90 days", as stated in the contract. */
export function formatNoticePeriod(days: number | null): string | null {
  return days === null ? null : `${days} days`;
}
